/* eslint-disable camelcase */
import React, {ReactElement, useEffect, useState} from 'react';
import {GridValidRowModel} from '@mui/x-data-grid-premium';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import moment from 'moment';
import {environment} from '@shared/config/environment';
import {useParams} from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  useTheme
} from '@mui/material';
import SelectInput from '@components/form/select-input/SelectInput';
import {
  ContainerGrid,
  BottomSectionGrid,
  FooterGrid,
  DocumentsGrid,
  RetentionInputGrid,
  DataGridComponentGrid,
  RetentionInput,
  FormControlGrid,
  SelectDropdownGrid
} from './ClinicalNewReportStyles';
import {
  generateReports,
  generateCustomReports,
  getPageData,
  createAuditLog
} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import AuthUtils from 'src/utils/AuthUtils';
import {useSelectedDocumentContext} from '@context/SelectedDocumentContext';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {jwtDecode} from 'jwt-decode';
import FormConstants from '@shared/constants/FormConstants';
import IsBhFilter from '@modules/home/main/sub-modules-menu/sub-module-page-section/IsBhFilter';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';
import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import {rowStyles} from '@shared/utils/HelperUtils';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import TabsComponent from '@modules/home/main/sub-modules-menu/tabsComponent/TabsComponent';
import {useSelectedDicomContext} from '@context/SelectedDicomContext';
import {ConfirmDialog} from '@components/custom-dialog';
import BasicDatePicker from '@components/form/date-picker/BasicDatePicker';
import dayjs from 'dayjs';

interface ClinicalReportProps {
  subModuleData: any;
  subModuleUIData: any;
  module: string;
  sub_module: string;
  patient_id?: string;
  legacyApp?: string;
  unique_id?: string;
  LegacyMRN?: any;
  handleGenerateReportBtn?: () => void;
  currentTab?: string;
  selectedTabData: any;
}

interface RetentionObj {
  label: string;
  maxValue: number | string;
  minValue: number | string;
  type: string;
  inputMode: 'numeric' | undefined;
}

interface ReportStateProps {
  selectName: string;
  dropDownValues: any;
  retentionNumberValue: number | string;
  rows: GridValidRowModel[];
  retentionInput: RetentionObj;
  selectedEncounters: any[];
  retentionDaysValue: number | string;
  retentionMonthsValue: number | string;
  retentionYearsValue: number | string;
  tableOfContentValue: number | boolean | string;
  coverPageWithLogo: number | boolean | string;
  supressComponentsWithNoData: number | boolean | string;
  includeRestrictedData: number | boolean | string;
  formError: string;
  isImagesAsZipEnabled: number | boolean | string;
}

const ClinicalNewReport = (props: ClinicalReportProps): React.ReactElement => {
  const theme = useTheme();
  const {
    appState: {applicationName}
  } = useAppContext();
  const {
    patient_id: patientID,
    module,
    sub_module,
    handleGenerateReportBtn,
    currentTab,
    selectedTabData
  } = props;

  // context
  const {SelectedDocumentState, clearSelectedDocumentData} =
    useSelectedDocumentContext();
  const {SelectedDicomState} = useSelectedDicomContext();
  // local storage
  const [selectDateName, setSelectDateName] = React.useState({
    start_date: null,
    stop_date: null
  });
  const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();
  const {enqueueSnackbar} = useSnackbar();
  const [state, setState] = useState<ReportStateProps>({
    formError: '',
    selectedEncounters: [],
    retentionNumberValue: 0,
    retentionMonthsValue: 0,
    retentionYearsValue: 0,
    retentionDaysValue: 0,
    tableOfContentValue: 0,
    coverPageWithLogo: 0,
    supressComponentsWithNoData: 0,
    isImagesAsZipEnabled: 0,
    includeRestrictedData: 0,
    selectName: FormConstants.REPORTS_TERMS.ALL_SYSTEMS,
    dropDownValues: {},
    rows: [],
    retentionInput: {
      label: '',
      maxValue: '',
      minValue: '',
      type: '',
      inputMode: 'numeric'
    }
  });
  const [isDialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = React.useState<boolean>(false);
  const [isButtonLoading, setButtonLoading] = React.useState<boolean>(false);
  const [isStartDateGreater, setIsStartDateGreater] = useState(false);

  const [isBHReason, setIsBHReason] = React.useState<string>('');
  const [selectedDropdownValue, setSelectedDropdownValue] = useState<string>(
    FormConstants.REPORTS_TERMS.ALL_SYSTEMS
  );
  const [pendingDropdownValue, setPendingDropdownValue] = useState<string>('');
  const [dropdownDisabled, setDropdownDisabled] = useState(false);
  const [selectedRadioOption, setSelectedRadioOption] = useState(
    FormConstants.REPORTS_TERMS.ALL
  );
  const [globalRadioDisabled, setGlobalRadioDisabled] = useState({
    [FormConstants.REPORTS_TERMS.ALL]: false,
    [FormConstants.REPORTS_TERMS.NONE]: false,
    [FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS]: false,
    [FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE]: false
  });

  let userData: any;

  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }
  // Radio Input States
  const [gridRadioOptionState, setGridRadioOptionState]: any = useState({
    options: [
      {
        radioName: 'PatientDemographics',
        selected: FormConstants.REPORTS_TERMS.ALL
      },
      {radioName: 'Insurances', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Relations', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Allergies', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Encounters', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Immunizations', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Medication', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Orders', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Results', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Transfusion', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Vitals', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Assessments', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Procedures', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'CarePlan', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Problems', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'History', selected: FormConstants.REPORTS_TERMS.ALL},
      {radioName: 'Task', selected: FormConstants.REPORTS_TERMS.ALL},
      {
        radioName: 'Allergy Reconciliation',
        selected: FormConstants.REPORTS_TERMS.ALL
      },
      {
        radioName: 'Problem Reconciliation',
        selected: FormConstants.REPORTS_TERMS.ALL
      },
      {
        radioName: 'Medication Reconciliation',
        selected: FormConstants.REPORTS_TERMS.ALL
      }
    ]
  });
  const {dropDownValues, formError} = state;

  // Check if any radioName is selected as FOR_SELECTED_ITEMS
  const isAnySelectedForSelectedItems = gridRadioOptionState?.options?.some(
    (option: any) =>
      option?.selected === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
  );

  const {t} = useTranslation();
  const params: any = useParams();
  const {
    control,
    getValues,
    reset,
    handleSubmit,
    setError,
    clearErrors,
    formState: {errors}
  } = useForm({
    mode: 'onChange'
  });
  const [activeRightViewTab, setActiveRightViewTab] = useState<string>(
    FormConstants.REPORTS_SCREENS.DOCUMENT_VIEWER_CLINICAL
  );
  const handleActiveTabChange = async (tabName: string) => {
    setActiveRightViewTab(tabName);
  };

  const handleDialogClose = () => {
    setPendingDropdownValue('');
    setDialogOpen(false);
  };

  const getDisableStatus = (item: any, value: string) => {
    const filterData = getLocaleStorageData
      ? JSON?.parse?.(getLocaleStorageData)
      : [];
    if (
      currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB &&
      item?.row?.label === FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
    ) {
      if (
        value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS &&
        filterData?.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS) {
        if (filterData?.length > 0) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    }
  };
  const getDisableStatusLeftMenu = (item: any, value: string) => {
    const filterData = getLocaleStorageData
      ? JSON?.parse?.(getLocaleStorageData)
      : [];

    if (
      currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB &&
      item?.row?.label === FormConstants.REPORTS_TERMS.ENCOUNTERS
    ) {
      if (
        value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS &&
        filterData?.length > 0 &&
        (selectedRadioOption ===
          FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.ALL ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.NONE)
      ) {
        return true;
      } else if (
        value === FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE &&
        (selectedRadioOption ===
          FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.ALL ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.NONE)
      ) {
        return true;
      } else {
        if (value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS) {
          return true;
        } else {
          return false;
        }
      }
    } else if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
      if (
        value === FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE &&
        selectedRadioOption === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
      ) {
        return true;
      } else if (
        value === FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE &&
        (selectedRadioOption === FormConstants.REPORTS_TERMS.ALL ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.NONE)
      ) {
        return true;
      } else if (
        value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS &&
        (selectedRadioOption ===
          FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.ALL ||
          selectedRadioOption === FormConstants.REPORTS_TERMS.NONE)
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS) {
        if (filterData?.length > 0) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    }
  };
  useEffect(() => {
    if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
      sessionStorage.removeItem('@checkedCount');

      setSelectedDropdownValue(FormConstants.REPORTS_TERMS.ALL_SYSTEMS);
      const filterData = getLocaleStorageData
        ? JSON.parse(getLocaleStorageData)
        : [];

      if (filterData.length > 0) {
        setSelectedRadioOption(FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS);

        // Update gridRadioOptionState based on presence of 'For Selected Encounters Only' in actions
        setGridRadioOptionState((prevState: {options: any[]}) => ({
          ...prevState,
          options: prevState.options.map(opt => {
            // Check if 'For Selected Encounters Only' is an available option for this item
            const hasForSelectedEncountersOnly =
              props?.subModuleUIData?.sub_sections[1]?.left_section_fields
                .find((field: any) => field.label === opt.radioName)
                ?.actions.some(
                  (action: any) =>
                    action.value ===
                    FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
                );

            return {
              ...opt,
              selected:
                hasForSelectedEncountersOnly &&
                (selectedRadioOption === FormConstants.REPORTS_TERMS.ALL ||
                  selectedRadioOption === FormConstants.REPORTS_TERMS.NONE ||
                  selectedRadioOption ===
                    FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS)
                  ? FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
                  : selectedRadioOption
                    ? selectedRadioOption
                    : FormConstants.REPORTS_TERMS.ALL
              // Preserve the existing selection if 'For Selected Encounters Only' is not available
            };
          })
        }));
      }
    } else {
      clearSelectedDocumentData();
      setSelectedRadioOption(FormConstants.REPORTS_TERMS.ALL);
      setSelectedDropdownValue(FormConstants.REPORTS_TERMS.ALL_SYSTEMS);
      setSelectDateName({start_date: null, stop_date: null}); //reset date range on tab change
      sessionStorage.removeItem('@checkedCount');
      setGridRadioOptionState((prevState: {options: any[]}) => ({
        ...prevState,
        options: prevState.options.map(opt => {
          return {
            ...opt,
            selected: FormConstants.REPORTS_TERMS.ALL
            // Preserve the existing selection if 'For Selected Encounters Only' is not available
          };
        })
      }));
    }
  }, [getLocaleStorageData, currentTab]);
  useEffect(() => {
    if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
      setPendingDropdownValue('');

      const updatedDisabledStatus = {
        [FormConstants.REPORTS_TERMS.ALL]: getDisableStatus(
          {row: {label: FormConstants.REPORTS_TERMS.ALL}},
          FormConstants.REPORTS_TERMS.ALL
        ),
        [FormConstants.REPORTS_TERMS.NONE]: getDisableStatus(
          {row: {label: FormConstants.REPORTS_TERMS.NONE}},
          FormConstants.REPORTS_TERMS.NONE
        ),
        [FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS]: getDisableStatus(
          {row: {label: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS}},
          FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
        ),
        [FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE]: getDisableStatus(
          {row: {label: FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE}},
          FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
        )
      };

      setGlobalRadioDisabled(updatedDisabledStatus);
    }
  }, [getLocaleStorageData, currentTab, selectDateName]);

  const handleRadioChange = (
    e: {target: {name: any; value: any}},
    isGlobalRadio?: boolean
  ) => {
    const {name, value} = e.target;
    const isGlobal = isGlobalRadio ?? false;

    if (isGlobal) {
      // Handle top-level radio button change

      const filterData = getLocaleStorageData
        ? JSON.parse(getLocaleStorageData)
        : [];

      setSelectedRadioOption(value);
      setGridRadioOptionState((prevState: {options: any[]}) => {
        const newOptions = prevState.options.map(opt => {
          const hasAction =
            props?.subModuleUIData?.sub_sections[1]?.left_section_fields
              .find((field: any) => field.label === opt.radioName)
              ?.actions.some((action: any) => action.value === value);

          // If other global options (!none) are selected, update specific items to 'ALL'
          if (
            value !== FormConstants.REPORTS_TERMS.NONE &&
            (opt.radioName ===
              FormConstants.REPORTS_TERMS.PATIENT_DEMOGRAPHICS ||
              opt.radioName === FormConstants.REPORTS_TERMS.INSURANCES ||
              opt.radioName === FormConstants.REPORTS_TERMS.RELATIONS)
          ) {
            return {
              ...opt,
              selected: FormConstants.REPORTS_TERMS.ALL
            };
          }

          // Prevent 'Encounters' from changing when 'filterData' exists
          if (
            opt.radioName === FormConstants.REPORTS_TERMS.ENCOUNTERS &&
            filterData?.length > 0 &&
            (value === FormConstants.REPORTS_TERMS.ALL ||
              value === FormConstants.REPORTS_TERMS.NONE ||
              value === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS)
          ) {
            return {
              ...opt,
              selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
            };
          } else if (
            opt.radioName === FormConstants.REPORTS_TERMS.ENCOUNTERS &&
            value === FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
          ) {
            return {
              ...opt,
              selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
            };
          }

          // Handle other fields normally
          return {
            ...opt,
            selected: hasAction ? value : opt.selected
          };
        });

        return {
          ...prevState,
          options: newOptions
        };
      });
    } else {
      // Handle individual radio button change
      setGridRadioOptionState((prevState: {options: any[]}) => ({
        ...prevState,
        options: prevState.options.map(opt => {
          const hasAction =
            props?.subModuleUIData?.sub_sections[1]?.left_section_fields
              .find((field: any) => field.label === opt.radioName)
              ?.actions.some((action: any) => action.value === value);

          return opt.radioName !== name
            ? {...opt}
            : {...opt, selected: hasAction ? value : opt.selected};
        })
      }));
    }
  };

  const getDefaultValue = (item: any) => {
    const filterData = getLocaleStorageData
      ? JSON?.parse?.(getLocaleStorageData)
      : [];

    if (
      item?.row?.label === FormConstants.REPORTS_TERMS.PATIENT_DEMOGRAPHICS ||
      item?.row?.label === FormConstants.REPORTS_TERMS.INSURANCES ||
      item?.row?.label === FormConstants.REPORTS_TERMS.RELATIONS
    ) {
      return FormConstants.REPORTS_TERMS.ALL;
    } else {
      if (filterData?.length > 0) {
        return FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS;
      } else {
        return selectedRadioOption;
      }
    }
  };

  const columns = [
    {
      field: 'label',
      width:
        currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS ? 410 : 200
    },
    {
      field: 'actions',
      flex: 1,
      renderCell: (data: any) => {
        const selectedValue =
          gridRadioOptionState.options.find(
            (opt: any) => opt.radioName === data.row.label
          )?.selected || getDefaultValue(data);

        // If any option is selected as FOR_SELECTED_ITEMS, force 'Encounters' to that value
        const enforcedValue =
          isAnySelectedForSelectedItems &&
          data.row.label === FormConstants.REPORTS_TERMS.ENCOUNTERS
            ? FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
            : selectedValue;

        return (
          <FormControl style={{flexDirection: 'row'}}>
            <RadioGroup
              row
              aria-labelledby="demo-radio-buttons-group-label"
              value={enforcedValue}
              name={data.row.label}
              onChange={e => handleRadioChange(e)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <div>
                {data?.value?.map((item: any) => (
                  <FormControlLabel
                    style={{fontSize: '15px'}}
                    aria-labelledby="demo-radio-buttons-group-label"
                    name={data.row.label}
                    key={item.id}
                    value={item.value}
                    disabled={getDisableStatusLeftMenu(data, item.value)}
                    control={<Radio style={{alignItems: 'center'}} />}
                    label={item.label}
                  />
                ))}
              </div>
            </RadioGroup>
          </FormControl>
        );
      }
    }
  ];

  const getOptions = async (values: any) => {
    try {
      const encryptRequestData = EncryptUtils.encryptedData(values);
      const res = getPageData(
        applicationName,
        params.module,
        params.sub_module,
        {
          tk: encryptRequestData
        }
      );
      return res;
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const init = async () => {
    setIsPageLoading(true);

    // resetting the form initially
    reset();
    setState(prevState => ({
      ...prevState,
      selectName: '',
      retentionNumberValue: 0,
      retentionMonthsValue: 0,
      retentionYearsValue: 0,
      retentionDaysValue: 0,
      tableOfContentValue: 0,
      coverPageWithLogo: 0,
      supressComponentsWithNoData: 0,
      isImagesAsZipEnabled: 0,
      formError: ''
    }));

    const showDDListPromiseArray: any = [];
    const showDDListKeys: any = [];
    Object.entries(props?.selectedTabData?.fields).forEach(
      async (item: any) => {
        const ddList: any = item;
        if (ddList[1].sql_data) {
          const sql = ddList[1].sql_data;
          const values = {
            sql_data: sql,
            user_id: patientID,
            legacyAppValue: ''
          };
          showDDListPromiseArray.push(getOptions(values));
          showDDListKeys.push(ddList[0]);
        }
      }
    );
    const dd = await Promise.all(showDDListPromiseArray);
    const dd1: any = {};
    dd.forEach((item: any, index: number) => {
      dd1[showDDListKeys[index]] = dd[index].data;
    });
    setState(prevState => ({
      ...prevState,
      dropDownValues: dd1
    }));

    let retentionValues =
      currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB ||
      currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
        ? selectedTabData.retention_fields
        : '';
    retentionValues = Object.values(retentionValues).map(item => {
      setState(prevState => ({
        ...prevState,
        retentionNumberValue: retentionValues?.max_value,
        retentionInput: {
          label: retentionValues?.label,
          maxValue: retentionValues?.max_value,
          minValue: retentionValues?.min_value,
          type: retentionValues?.type,
          inputMode: retentionValues?.input_mode
        }
      }));
    });

    const filterData = getLocaleStorageData
      ? JSON?.parse?.(getLocaleStorageData)
      : [];

    if (filterData?.length > 0) {
      // Extract all LegacyApp values
      const allLegacyApps = filterData.map((item: any) => item.LegacyApp);

      // Use Set to filter out duplicates
      const uniqueLegacyApps: string[] = [...new Set<string>(allLegacyApps)];
      if (uniqueLegacyApps?.length > 1) {
        setSelectedDropdownValue(FormConstants.REPORTS_TERMS.ALL_SYSTEMS);
      } else {
        setSelectedDropdownValue(uniqueLegacyApps[0]);
      }
      setDropdownDisabled(true);

      setGridRadioOptionState({
        options: [
          {
            radioName: 'PatientDemographics',
            selected: FormConstants.REPORTS_TERMS.ALL
          },
          {radioName: 'Insurances', selected: FormConstants.REPORTS_TERMS.ALL},
          {radioName: 'Relations', selected: FormConstants.REPORTS_TERMS.ALL},
          {
            radioName: 'Allergies',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Encounters',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Immunizations',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Medication',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Orders',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Results',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Transfusion',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Vitals',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Assessments',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Procedures',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'CarePlan',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Problems',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'History',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Task',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Allergy Reconciliation',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Problem Reconciliation',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          },
          {
            radioName: 'Medication Reconciliation',
            selected: FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
          }
        ]
      });
    }

    setIsPageLoading(false);
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabData]);

  // get legacy dropdown value
  const handleDropdownSelectChange = async (
    inputName: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setState(prevState => ({
      ...prevState,
      formError: ''
    }));
    const selectedValue =
      event?.target?.value || FormConstants.REPORTS_TERMS.ALL_SYSTEMS;

    if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
      // Case 1: Switching between "All Systems" and any legacy system (no confirmation required)
      if (
        selectedValue === FormConstants.REPORTS_TERMS.ALL_SYSTEMS
        // || selectedDropdownValue === FormConstants.REPORTS_TERMS.ALL_SYSTEMS
        // || selectedValue === selectedDropdownValue
      ) {
        setSelectedDropdownValue(selectedValue);
        setIsConfirmed(true);
      }
      // Case 2: Switching between different legacy systems (show confirmation popup)
      else {
        setPendingDropdownValue(selectedValue);
        setDialogOpen(true);
      }
    } else if (
      currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS &&
      inputName === 'legacyApp' &&
      event?.target?.value
    ) {
      const fieldsArray = props?.selectedTabData?.fields[1] || {};
      const entries: any = Object.values(fieldsArray);
      const value: any = {
        sql_data: entries?.[0].sql_data,
        legacyAppValue: event?.target?.value
      };
      const res = await getOptions(value);
      setState((prevState: any) => ({
        ...prevState,
        dropDownValues: {...prevState.dropDownValues, ReportType: res.data}
      }));
    }
  };

  const getSelectedRadioValue = (key: string) => {
    const result = gridRadioOptionState?.options?.find?.(
      (item: any) => item.radioName === key
    );
    return result?.selected;
  };

  const getEncountersSelectedValue = () => {
    const encounterValue = getSelectedRadioValue(
      FormConstants.REPORTS_TERMS.ENCOUNTERS
    );

    if (
      encounterValue === FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS ||
      isAnySelectedForSelectedItems
    ) {
      const filterData = getLocaleStorageData
        ? JSON.parse(getLocaleStorageData)
        : [];
      const SelectedEncountersList = filterData?.map((item: any) => {
        return item?.EncounterNumber;
      });

      return SelectedEncountersList?.length > 0
        ? SelectedEncountersList.join(',')
        : null;
    }

    return encounterValue;
  };

  const getDocumentsSelectedValue = () => {
    const docData: any = SelectedDocumentState?.selectionData?.map(
      (item: any) => {
        // Split the stored value by ' $ ' and extract the ID (before the separator)
        return item.split(FormConstants.REPORTS_TERMS.DOCUMENTS_SEPARATOR)[0];
      }
    );
    if (docData?.length > 0) {
      return docData.join(',');
    }
    return FormConstants.REPORTS_TERMS.NONE; // Return 'NONE' if no data
  };

  const getDicomSelectedValue = () => {
    const dicomData: any = [...SelectedDicomState.selectionData];
    if (dicomData?.length > 0) {
      return dicomData.join(',');
    }
    return null; // Return null if no data
  };

  const handleCheckBoxList = (data: any, inputName: string) => {
    data = data === true ? 1 : 0;
    if (inputName === 'table_of_content') {
      setState(prevState => ({
        ...prevState,
        tableOfContentValue: data
      }));
    }
    if (inputName === 'cover_page_with_logo') {
      setState(prevState => ({
        ...prevState,
        coverPageWithLogo: data
      }));
    }
    if (inputName === 'supress_components_with_no_data') {
      setState(prevState => ({
        ...prevState,
        supressComponentsWithNoData: data
      }));
    }
    if (inputName === 'include_restricted_data_select_the_reason_below') {
      setState(prevState => ({
        ...prevState,
        includeRestrictedData: data
      }));
    }
    if (inputName === 'zip_images_separately') {
      setState(prevState => ({
        ...prevState,
        isImagesAsZipEnabled: data
      }));
    }
  };

  const logReportsData = (payloadData: any) => {
    const dataObjs: any = {...payloadData};

    if (dataObjs.payload?.[0].PatientID) {
      dataObjs.payload[0] = {
        'Report Level': 'MPI Level',
        ...dataObjs.payload[0]
      };
    } else {
      dataObjs.payload[0] = {
        'Report Level': 'MRN Level',
        ...dataObjs.payload[0]
      };
    }

    // delete dataObjs.payload?.[0].PatientID;

    const keyValueString = Object.entries(dataObjs.payload?.[0])
      .map(([key, value]) => {
        if (value && key != 'PatientID') {
          return `${key}: ${JSON.stringify(value)}`;
        }
        return null;
      })
      .filter(Boolean)
      .join(', ');

    let eventDescription = params.module_name
      ? params.module_name + ' -> '
      : '';

    if (params.submodule_name) {
      eventDescription += `${params.submodule_name}`;
    }

    if (dataObjs?.sql_data?.audit_event_description) {
      eventDescription += ` -> ${dataObjs?.sql_data?.audit_event_description}`;
    } else if (dataObjs?.name) {
      eventDescription += ` -> ${dataObjs?.name}`;
    }

    const data = {
      module: params.module_name,
      sub_module:
        params.submodule_name +
        ` -> ${dataObjs?.sql_data?.audit_event_description}`,
      sub_module_identifier: keyValueString,
      application_name: applicationName,
      isBH_selected: !isBHReason?.length ? true : false,
      isBH_reason: isBHReason,
      event_description: eventDescription,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(props.module, data);
  };

  const onSubmit = async (dataValues: any) => {
    const submitData = Object.entries(dataValues);
    let isFormHasError = false;
    submitData.forEach((item: any[]) => {
      if (currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS && item) {
        if (!item[1]) {
          setState(prevState => ({
            ...prevState,
            formError: 'Please select all values'
          }));
          isFormHasError = true;
        }
      }
    });
    let startDate: string | null = null;
    let endDate: string | null = null;
    if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
      const start_date =
        dataValues?.start_date != null ? new Date(dataValues?.start_date) : '';
      const stop_date =
        dataValues?.stop_date != null ? new Date(dataValues.stop_date) : '';
      startDate = start_date && start_date.toLocaleDateString('en-US');
      endDate = stop_date && stop_date.toLocaleDateString('en-US');
      let dateerror;
      if (startDate) {
        dateerror = true;
      }
      if (endDate) {
        endDate = `${endDate} 23:59:59`;

        dateerror = true;
      }
      if (startDate && endDate) {
        const startDateParsed = moment(startDate, 'M/D/YYYY HH:mm:ss'); // Assuming format is 'M/D/YYYY HH:mm:ss'
        const endDateParsed = moment(endDate, 'M/D/YYYY HH:mm:ss'); // Assuming format is 'M/D/YYYY HH:mm:ss'
        if (startDateParsed.isAfter(endDateParsed)) {
          dateerror = true;
        }
        // else if (startDateParsed.isSame(endDateParsed)) {
        //   setState(prevState => ({
        //     ...prevState,
        //     formError: t('Start date is same as end date.')
        //   }));
        // }
        else if (startDateParsed.isBefore(endDateParsed)) {
          dateerror = false;
        } else {
          dateerror = false;
        }
      }
      if (dateerror) {
        setIsStartDateGreater(true);
        isFormHasError = true;
        return;
      }
      // if (!startDate?.length && !endDate?.length) {
      //   setIsFormDirty(true);
      //   return;
      // }
    }
    if (isFormHasError) return;
    try {
      const data = getValues();
      let selectedLegacyAppValues: any = [];

      if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
        if (data?.legacy_system === '') {
          selectedLegacyAppValues = [{patientID: patientID}];
        } else {
          const result = dropDownValues?.legacy_system?.find?.(
            (item: any) => item.LegacyApp === data.legacy_system
          );
          selectedLegacyAppValues.push(result);
        }
      }

      const list: any = [];
      let obj: any;

      if (currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS) {
        const legacyAppResult = dropDownValues?.['legacyApp']?.find?.(
          (item: any) => item.LegacyApp === data.legacyApp
        );
        const reportTypeResult = dropDownValues?.['ReportType']?.find?.(
          (item: any) => item.ReportType === data.ReportType
        );
        obj = {
          ModuleName: 'clinical',
          LegacyApp: data.legacyApp,
          LegacyMRN: legacyAppResult?.LegacyMRN,
          BytePadCreatedDate: moment(),
          BytePadUpdatedDate: moment(),
          Encounters: getEncountersSelectedValue(),
          retentionYears: state.retentionYearsValue,
          retentionMonths: state.retentionMonthsValue,
          RetentionTimeInDays: state.retentionDaysValue,
          isTableOfContentEnabled: state.tableOfContentValue,
          isCoverPageWithLogoEnabled: state.coverPageWithLogo,
          isSuppressComponentsWithNoDataEnabled:
            state.supressComponentsWithNoData,
          GeneratedBy: userData?.user?.id,
          GenerationDate: moment(),
          LogType: 'GENERATE_INITIATED',
          UpdatedBy: userData?.user?.id,
          isBH: state.includeRestrictedData ? 1 : 0,
          isBH_Message: isBHReason,
          ReportTypeID: reportTypeResult?.ID || '', // id of report type,
          isImagesAsZipEnabled: state.isImagesAsZipEnabled,
          StartDate: startDate,
          EndDate: endDate
        };
      } else {
        // check this
        const selectedDetailsListObj = {
          ModuleName: FormConstants.REPORTS_TERMS.CLINICAL,
          PatientID: selectedLegacyAppValues?.[0]?.patientID,
          LegacyApp: selectedLegacyAppValues?.[0]?.LegacyApp,
          LegacyMRN: selectedLegacyAppValues?.[0]?.LegacyMRN,
          BytePadCreatedDate: moment(),
          BytePadUpdatedDate: moment(),
          PatientDemographics: getSelectedRadioValue('PatientDemographics'),
          Insurances: getSelectedRadioValue('Insurances'),
          Relations: getSelectedRadioValue('Relations'),
          Allergies: getSelectedRadioValue('Allergies'),
          Encounters: getEncountersSelectedValue(),
          Immunizations: getSelectedRadioValue('Immunizations'),
          Medication: getSelectedRadioValue('Medication'),
          Orders: getSelectedRadioValue('Orders'),
          Results: getSelectedRadioValue('Results'),
          Transfusion: getSelectedRadioValue('Transfusion'),
          Vitals: getSelectedRadioValue('Vitals'),
          Assessments: getSelectedRadioValue('Assessments'),
          Procedures: getSelectedRadioValue('Procedures'),
          CarePlan: getSelectedRadioValue('CarePlan'),
          Problems: getSelectedRadioValue('Problems'),
          History: getSelectedRadioValue('History'),
          Task: getSelectedRadioValue('Task'),
          AllergyReconciliation: getSelectedRadioValue(
            'Allergy Reconciliation'
          ),
          ProblemReconciliation: getSelectedRadioValue(
            'Problem Reconciliation'
          ),
          MedicationReconciliation: getSelectedRadioValue(
            'Medication Reconciliation'
          ),
          Documents: getDocumentsSelectedValue(),
          csv_SOPInstanceUID: getDicomSelectedValue(),
          retentionYears: state.retentionYearsValue,
          retentionMonths: state.retentionMonthsValue,
          RetentionTimeInDays: state.retentionDaysValue,
          isTableOfContentEnabled: state.tableOfContentValue,
          isCoverPageWithLogoEnabled: state.coverPageWithLogo,
          isSuppressComponentsWithNoDataEnabled:
            state.supressComponentsWithNoData,
          GeneratedBy: userData?.user?.id,
          GenerationDate: moment(),
          LogType: FormConstants.REPORTS_TERMS.GENERATE_INITIATED,
          UpdatedBy: userData?.user?.id,
          isBH: state.includeRestrictedData ? 1 : 0,
          isBH_Message: isBHReason,
          isImagesAsZipEnabled: state.isImagesAsZipEnabled,
          StartDate: startDate,
          EndDate: endDate
        };

        list.push(selectedDetailsListObj);
      }

      const dataObj: any = {
        payload:
          currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
            ? list
            : currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
              ? obj
              : '',
        generationDtlSQL:
          currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
            ? props?.subModuleUIData?.sub_sections?.[1]?.sql_data
                ?.generation_sql
            : currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
              ? props?.subModuleUIData?.sub_sections?.[2]?.sql_data
                  ?.generation_sql
              : '',
        allSystemGenerationDtlSQL:
          currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
            ? props?.subModuleUIData?.sub_sections?.[1]?.sql_data
                ?.all_systems_generation_sql
            : '',
        generationDtlAuditLogsSQL:
          currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
            ? props?.subModuleUIData?.sub_sections?.[1]?.sql_data
                ?.generation_audit_logs_sql
            : currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
              ? props?.subModuleUIData?.sub_sections?.[2]?.sql_data
                  ?.generation_audit_logs_sql
              : '',
        audit_identifier: AuthUtils.getAuditIdentifier(),
        audit_identifier_type: AuthUtils.getAuditIdentifierType(),
        audit_legacy_app: AuthUtils.getAuditLegacyApp(),
        isBH: state.includeRestrictedData ? 1 : 0,
        isBH_Message: isBHReason,
        isImagesAsZipEnabled: state.isImagesAsZipEnabled
      };

      const auditObj: any = {
        is_audit: true
      };

      if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
        auditObj.audit_event_description =
          FormConstants.COMMON.GENERATED_STANDARD_PSR_REPORTS;
      } else {
        auditObj.audit_event_description =
          FormConstants.COMMON.GENERATED_CUSTOMS_REPORTS;
      }

      dataObj.sql_data = auditObj;

      logReportsData(dataObj);

      const encryptRequestData = EncryptUtils.encryptedData(dataObj);
      setButtonLoading(true);
      let res;
      if (currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB) {
        res = await generateReports(module, sub_module, {
          tk: encryptRequestData
        });
      } else if (currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS) {
        res = await generateCustomReports(module, sub_module, {
          tk: encryptRequestData
        });
      }

      setButtonLoading(false);
      enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
      if (handleGenerateReportBtn) handleGenerateReportBtn();
    } catch (e) {
      setButtonLoading(false);
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleRetentionInput = (label: string, e: any) => {
    let value = e.target.value;

    // Ensure at most one leading zero
    if (value.length > 1 && value.startsWith('0')) {
      value = value.replace(/^0+/, ''); // Replace multiple leading zeros with a single zero
    }

    // Convert value to number to prevent invalid inputs
    const numericValue = Number(value);

    if (label === 'retention_days') {
      if (numericValue <= 31) {
        setState(prevState => ({
          ...prevState,
          retentionDaysValue: value
        }));
      }
    }
    if (label === 'retention_months') {
      if (numericValue <= 12) {
        setState(prevState => ({
          ...prevState,
          retentionMonthsValue: value
        }));
      }
    }
    if (label === 'retention_years') {
      if (numericValue <= 99) {
        setState(prevState => ({
          ...prevState,
          retentionYearsValue: value
        }));
      }
    }
  };

  // retention fields don't allow negative values from keyboard
  const preventMinus = (e: {code: string; preventDefault: () => void}) => {
    if (
      e.code === 'Minus' ||
      // e.code === 'Numpad1' ||
      e.code === 'NumpadSubtract'
    ) {
      e.preventDefault();
    }
  };

  const renderRetentionFields = () => {
    const retentionFields =
      currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB ||
      currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
        ? selectedTabData.retention_fields
        : '';
    const checkBoxFields =
      currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
        ? props?.subModuleUIData?.sub_sections[2].check_box_fields
        : props?.subModuleUIData?.sub_sections[1].check_box_fields;
    return (
      <RetentionInputGrid style={{display: 'flex', flexDirection: 'column'}}>
        {Object.values(retentionFields).map((item: any, index: number) => (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '0.3rem'
            }}>
            <div style={{flex: 1, minWidth: '25rem'}}>{item.label}</div>
            <div style={{flex: 1}}>
              <FormControlLabel
                style={{marginRight: '1rem'}}
                value={item.label}
                name={item.name}
                control={
                  <RetentionInput
                    type={item.type}
                    inputMode={item.input_mode}
                    min={item.min_value}
                    max={item.max_value}
                    onKeyPress={preventMinus}
                    value={
                      item.name === 'retention_days'
                        ? state.retentionDaysValue
                        : item.name === 'retention_months'
                          ? state.retentionMonthsValue
                          : item.name === 'retention_years'
                            ? state.retentionYearsValue
                            : ''
                    }
                    onChange={(e: any) => {
                      handleRetentionInput(item.name, e);
                    }}
                  />
                }
                label={''}
                labelPlacement={'start'}
              />
            </div>
          </div>
        ))}
        {Object.values(checkBoxFields).map((item: any, index: number) => {
          const shouldShowCheckbox =
            item.name !== 'zip_images_separately' ||
            environment?.isConvertTozip;
          if (!shouldShowCheckbox) {
            return null; // Don't render the checkbox if the condition is not met
          }
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '0.3rem'
              }}>
              <div style={{flex: 1, minWidth: '24.8rem'}}>{item.label}</div>
              <div style={{flex: 1}}>
                <FormControlLabel
                  value={item.label}
                  control={<Checkbox />}
                  label={''}
                  labelPlacement={'start'}
                  onChange={(e: any) => {
                    handleCheckBoxList(e.target.checked, item.name);
                  }}
                />
              </div>
            </div>
          );
        })}
      </RetentionInputGrid>
    );
  };

  const renderSubmitButton = () => {
    return (
      <Grid>
        {isStartDateGreater ? (
          <div style={{color: 'red', marginBottom: '0.5rem'}}>
            Start Date is Greater Than Stop Date
          </div>
        ) : null}
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isButtonLoading}
          disabled={
            isButtonLoading ||
            isStartDateGreater ||
            (!!state.includeRestrictedData && !(isBHReason?.length > 0))
          }
          onClick={() => handleSubmit(onSubmit)()}
          sx={{
            backgroundColor: theme.palette.primary.dark
          }}>
          {t('PRIVATE.REPORTS.GENERATE_REPORT', {ns: 'private'})}
        </LoadingButton>
      </Grid>
    );
  };

  const renderIsBHDropdown = () => {
    return (
      <IsBhFilter
        module={module}
        optionValue={isBHReason}
        setOptionValue={setIsBHReason}
      />
    );
  };

  const renderFooterSection = () => {
    return (
      <Grid
        container
        spacing={1}
        sx={{display: 'flex', flexDirection: 'column'}}>
        <Grid item>
          <FooterGrid>{renderRetentionFields()}</FooterGrid>
        </Grid>
        {!!state.includeRestrictedData && (
          <Grid item sx={{mx: 2}}>
            {renderIsBHDropdown()}
          </Grid>
        )}
        <Grid
          item
          style={{
            display: 'flex',
            justifyContent: 'start',
            marginLeft: '1.2rem'
          }}>
          {renderSubmitButton()}
        </Grid>
      </Grid>
    );
  };

  const renderSelectDropdownFields = (
    inputName: string,
    label: string
  ): ReactElement => {
    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={''}
        render={({field: {value, onChange}}) => (
          <FormControlGrid style={{paddingRight: '8px'}}>
            <Grid style={{marginBottom: '5px'}}>{label}</Grid>
            <SelectInput
              label={''}
              options={dropDownValues[inputName] || []}
              placeholder={
                currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
                  ? FormConstants.REPORTS_TERMS.ALL_SYSTEMS
                  : 'Select'
              }
              name={
                currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
                  ? inputName || FormConstants.REPORTS_TERMS.ALL_SYSTEMS
                  : inputName
              }
              value={
                currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
                  ? selectedDropdownValue
                    ? selectedDropdownValue
                    : value || FormConstants.REPORTS_TERMS.ALL_SYSTEMS
                  : currentTab === FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
                    ? value || 'Select'
                    : value
              }
              fullWidth
              onChange={(e: any) => {
                onChange(e);
                handleDropdownSelectChange(inputName, e);
              }}
              optionValueKey={
                ['legacyApp', 'legacy_system'].includes(inputName)
                  ? 'LegacyApp'
                  : inputName
              }
              optionLabelKey={
                ['legacyApp', 'legacy_system'].includes(inputName)
                  ? 'LegacyApp'
                  : inputName
              }
              enableDefaultOption={
                currentTab !== FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
              }
              disabled={
                activeRightViewTab ===
                FormConstants.REPORTS_SCREENS.DICOM_VIEWER_CLINICAL // Disable when right tab is DICOM
                  ? true
                  : currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
                    ? dropdownDisabled
                    : false
              }
            />
          </FormControlGrid>
        )}
      />
    );
  };

  const renderLeftSectionDataGrid = () => {
    return (
      <Grid item xs={6.2}>
        <Grid item xs={12}>
          <div>
            <DataGridComponentGrid>
              <StripedDataGrid
                rows={
                  currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB
                    ? props?.subModuleUIData?.sub_sections[1]
                        ?.left_section_fields
                    : currentTab ===
                        FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
                      ? props?.subModuleUIData?.sub_sections[2]
                          ?.left_section_fields
                      : []
                }
                columns={columns}
                autoHeight={true}
                columnHeaderHeight={0}
                getRowClassName={(params: any) => rowStyles(params)}
                rowHeight={37}
                hideFooter
              />
            </DataGridComponentGrid>
          </div>
        </Grid>
        {renderFooterSection()}
      </Grid>
    );
  };

  const renderSelectDropdowns = (field: any) => {
    const fields: any = field?.[0];
    if (fields && field[1].sql_data) {
      return renderSelectDropdownFields(fields, field[1]?.heading);
    }
    return null;
  };
  const renderDateField = (
    inputName: keyof typeof selectDateName,
    item: any
  ): ReactElement => {
    return (
      <div>
        <Controller
          name={inputName}
          control={control}
          defaultValue={selectDateName[inputName] || null}
          render={({field}) => (
            <BasicDatePicker
              value={
                selectDateName[inputName]
                  ? dayjs(selectDateName[inputName])
                  : null
              }
              label={item?.heading || ''}
              error={!!errors[inputName]}
              helperText={
                `${errors[inputName]?.message}` ||
                ` ${item?.heading} is required`
              }
              disabled={
                selectedRadioOption !==
                FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
              }
              field={field}
              handleChange={date => {
                const parsed = dayjs(date);

                setIsStartDateGreater(false);

                const fullDateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

                // If invalid dayjs input, keep raw value + error
                if (!parsed.isValid()) {
                  setSelectDateName(prevState => ({
                    ...prevState,
                    [inputName]: date
                  }));
                  setError(inputName, {
                    type: 'manual',
                    message: 'Invalid date input'
                  });
                  return;
                }

                const formattedDate = parsed.format('MM/DD/YYYY');
                const year = parsed.year();

                // Proceed only if format is correct and year > 1900
                if (!fullDateRegex.test(formattedDate) || year <= 1900) {
                  setError(inputName, {
                    type: 'manual',
                    message: 'Date must be complete and year > 1900'
                  });
                  return;
                }

                // Build the next values to compare (use the new date + existing other date)
                const nextValues = {
                  ...selectDateName,
                  [inputName]: formattedDate
                };

                const startStr = nextValues.start_date;
                const endStr = nextValues.stop_date;

                const start = startStr
                  ? dayjs(startStr, 'MM/DD/YYYY', true)
                  : null;
                const end = endStr ? dayjs(endStr, 'MM/DD/YYYY', true) : null;

                // If both exist and are valid, enforce start < end (or <= if you prefer)
                if (start?.isValid() && end?.isValid()) {
                  if (start.isAfter(end)) {
                    setIsStartDateGreater(true);

                    // Put the error on the field being edited (common UX)
                    setError(inputName, {
                      type: 'manual',
                      message:
                        'Start date must be less than or equal to end date'
                    });

                    // Optionally also set error on the other field:
                    // setError(inputName === 'start_date' ? 'end_date' : 'start_date', {
                    //   type: 'manual',
                    //   message: 'Date range is invalid'
                    // });

                    return;
                  } else {
                    // Clear both range-related errors when valid
                    clearErrors('start_date');
                    clearErrors('stop_date');
                  }
                }

                // Save valid date
                setSelectDateName(prevState => ({
                  ...prevState,
                  [inputName]: formattedDate
                }));
                clearErrors(inputName);
              }}
              sx={{paddingRight: '8px'}}
            />
          )}
        />
      </div>
    );
  };
  const renderRightSectionDocument = () => {
    return (
      <DocumentsGrid item xs={5} md={6} lg={5.7}>
        <TabsComponent
          sectionUIData={
            props?.subModuleUIData?.sub_sections?.[1]?.right_section_view
          }
          sectionRowsData={[]}
          module={props?.module}
          sub_module={props?.sub_module}
          patient_id={patientID}
          legacyApp={props?.legacyApp}
          unique_id={props?.unique_id}
          selectedDropdownValue={selectedDropdownValue}
          isConfirmed={isConfirmed}
          setIsConfirmed={setIsConfirmed}
          handleActiveTabChange={handleActiveTabChange}
          selectedDateValue={selectDateName}
          selectedRadioOption={selectedRadioOption}
        />
      </DocumentsGrid>
    );
  };

  return (
    <>
      <Card
        sx={{
          p: 1,
          mt: 1,
          boxShadow: 5,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: '300px'
        }}>
        {isPageLoading ? (
          <Stack
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            minHeight={'80vh'}>
            <LoadingScreen />
          </Stack>
        ) : (
          <ContainerGrid sx={{p: 1}}>
            <Grid
              container
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                alignItems: 'end'
              }}>
              {selectedTabData?.fields &&
                Object.entries(selectedTabData?.fields)?.map(
                  (field: any, index: number) => {
                    const fieldMeta = field[1];
                    const fieldKey = field[0];
                    const type = fieldMeta?.type;

                    // Date field
                    if (type === FormConstants.FIELD_TYPES.DATE) {
                      return renderDateField(fieldKey, fieldMeta);
                      // or wrap in Grid item if needed:
                      // return (
                      //   <Grid item xs={...} key={index}>
                      //     {renderDateField(fieldKey, fieldMeta)}
                      //   </Grid>
                      // );
                    }

                    // Select field
                    if (type === FormConstants.FIELD_TYPES.SELECT) {
                      const dropdownContent = renderSelectDropdowns(field);

                      if (!dropdownContent) return null;

                      return (
                        <SelectDropdownGrid
                          item
                          xs={
                            currentTab ===
                            FormConstants.REPORTS_SCREENS.CUSTOM_REPORTS
                              ? 3
                              : 2
                          }
                          key={index}
                          style={{display: 'flex'}}>
                          {dropdownContent}
                        </SelectDropdownGrid>
                      );
                    }

                    // Other field types – render nothing
                    return null;
                  }
                )}
            </Grid>
            {/* Global Radio Buttons*/}
            {currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB && (
              <RadioGroup
                row
                aria-labelledby="demo-radio-buttons-group-label"
                value={selectedRadioOption}
                name="radio-buttons-group"
                onChange={e => handleRadioChange(e, true)}
                sx={{
                  mt: '5px',
                  ml: '16px',
                  mb: '-16px'
                }}>
                <FormControlLabel
                  value={FormConstants.REPORTS_TERMS.ALL}
                  control={<Radio />}
                  label={FormConstants.REPORTS_TERMS.ALL}
                  disabled={
                    globalRadioDisabled[FormConstants.REPORTS_TERMS.ALL]
                  }
                />
                <FormControlLabel
                  value={FormConstants.REPORTS_TERMS.NONE}
                  control={<Radio />}
                  label={FormConstants.REPORTS_TERMS.NONE}
                  disabled={
                    globalRadioDisabled[FormConstants.REPORTS_TERMS.NONE]
                  }
                />
                <FormControlLabel
                  value={FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS}
                  control={<Radio />}
                  label={FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS}
                  disabled={
                    globalRadioDisabled[
                      FormConstants.REPORTS_TERMS.FOR_SELECTED_ITEMS
                    ]
                  }
                />
                <FormControlLabel
                  value={FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE}
                  control={<Radio />}
                  label={FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE}
                  disabled={
                    globalRadioDisabled[
                      FormConstants.REPORTS_TERMS.FOR_SELECTED_DATE_RANGE
                    ]
                  }
                />
              </RadioGroup>
            )}

            {formError && (
              <div style={{color: 'red', margin: '0.5rem 1rem 0 '}}>
                {formError}
              </div>
            )}
            <BottomSectionGrid>
              {renderLeftSectionDataGrid()}
              {currentTab === FormConstants.REPORTS_SCREENS.NEW_REPORT_TAB &&
                renderRightSectionDocument()}
            </BottomSectionGrid>
          </ContainerGrid>
        )}
      </Card>
      <ConfirmDialog
        open={isDialogOpen}
        onClose={() => handleDialogClose()}
        title="Confirm Legacy App Change"
        content={
          <>
            Switching to <strong>{pendingDropdownValue}</strong> will clear your
            current selections from <strong>{selectedDropdownValue}</strong>. Do
            you want to continue?
          </>
        }
        action={
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setIsConfirmed(true);
              setSelectedDropdownValue(pendingDropdownValue);
              handleDialogClose();
            }}>
            Confirm
          </Button>
        }
      />
    </>
  );
};

export default ClinicalNewReport;

